import React from 'react';
import { Box, Button, CircularProgress, IconButton, styled, Typography, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';

import { QRCodeSVG } from 'qrcode.react';
import { useSnackbar } from 'notistack';

import { MuiModal } from 'ui/MuiModal';
import { ITrackingLink } from '../trackingLinks';
import { copyToClipboard } from 'lib/helpers';

const StyledQRCodeContainer = styled(Box)(({ theme }) => ({
    width: 'fit-content',
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    margin: '0 auto'
}));

interface DownloadQRModalProps {
    open: boolean;
    link?: ITrackingLink;
    shortLinkUrl: string;
    onClose: () => void;
}

const QR_SIZE = 250;

export const DownloadQRModal = ({ open, link, shortLinkUrl, onClose }: DownloadQRModalProps) => {
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const qrContainerRef = React.useRef<HTMLDivElement>(null);
    const [isDownloading, setIsDownloading] = React.useState(false);

    const handleCopyLink = React.useCallback(() => {
        if (!shortLinkUrl) {
            return;
        }
        try {
            copyToClipboard(shortLinkUrl);
            enqueueSnackbar('Link copied to clipboard', { variant: 'info' });
        } catch {
            enqueueSnackbar('Failed to copy link', { variant: 'error' });
        }
    }, [shortLinkUrl, enqueueSnackbar]);

    const handleDownloadPNG = React.useCallback(async () => {
        const svg = qrContainerRef.current?.querySelector('svg');
        if (!svg) {
            enqueueSnackbar('QR code is not ready yet', { variant: 'error' });
            return;
        }
        setIsDownloading(true);
        try {
            const svgString = new XMLSerializer().serializeToString(svg);
            const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(svgBlob);

            const dataURL = await new Promise<string>((resolve, reject) => {
                const img = new Image();
                const adjustedSize = QR_SIZE * 2;
                img.onload = () => {
                    try {
                        const canvas = document.createElement('canvas');
                        canvas.width = adjustedSize;
                        canvas.height = adjustedSize;
                        const ctx = canvas.getContext('2d');
                        if (!ctx) {
                            reject(new Error('Could not get canvas context'));
                            return;
                        }
                        // adjust the size of the QR code to ensure it is sharp
                        ctx.drawImage(img, 0, 0, adjustedSize, adjustedSize);
                        resolve(canvas.toDataURL('image/png'));
                    } catch (err) {
                        reject(err instanceof Error ? err : new Error(String(err)));
                    }
                };
                img.onerror = () => {
                    reject(new Error('Failed to load QR image'));
                };
                img.src = url;
            });

            URL.revokeObjectURL(url);

            const a = document.createElement('a');
            a.href = dataURL;
            a.download = `${link?.internalTitle || 'qr-code'}.png`;
            a.click();
            enqueueSnackbar('QR code downloaded', { variant: 'success' });
        } catch {
            enqueueSnackbar('Failed to download QR code', { variant: 'error' });
        } finally {
            setIsDownloading(false);
        }
    }, [link?.internalTitle, enqueueSnackbar]);
    return (
        <MuiModal open={open} onClose={onClose}>
            <Box display="flex" flexDirection="column" gap={3} width="400px">
                <Box>
                    <Typography variant="h5" color="textSecondary" fontWeight="bold">
                        QR Code
                    </Typography>
                    <Box position="absolute" top={4} right={4}>
                        <IconButton size="large" onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>
                <Box>
                    <Typography variant="body1" textAlign="center">
                        {link?.internalTitle}
                    </Typography>
                </Box>
                <StyledQRCodeContainer ref={qrContainerRef}>
                    <Box padding={2}>
                        <QRCodeSVG
                            value={shortLinkUrl}
                            fgColor={theme.palette.text.secondary}
                            // bgColor="none"
                            bgColor={theme.palette.background.default}
                            size={QR_SIZE}
                        />
                    </Box>
                </StyledQRCodeContainer>
                <Box display="flex" flexDirection="row" gap={1} justifyContent="space-between" width="100%">
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleCopyLink}
                        disabled={isDownloading}
                    >
                        Copy Link
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleDownloadPNG}
                        disabled={isDownloading || !shortLinkUrl}
                        startIcon={
                            isDownloading ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <DownloadIcon fontSize="small" />
                            )
                        }
                    >
                        {isDownloading ? 'Downloading...' : 'Download PNG'}
                    </Button>
                </Box>
            </Box>
        </MuiModal>
    );
};
